import React, { useState } from 'react';
import { VoiceControllerState } from '../voice/voice_controller';
import { ScrollEngineState } from '../core/scroll/types';
import { X, Copy, Check } from 'lucide-react';

interface DiagnosticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  voiceState: VoiceControllerState | null;
  scrollState: ScrollEngineState;
}

export const DiagnosticsModal: React.FC<DiagnosticsModalProps> = ({
  isOpen,
  onClose,
  voiceState,
  scrollState,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const diagnosticsData = {
    timestamp: new Date().toISOString(),
    scroll: {
      y: Math.round(scrollState.y),
      maxY: Math.round(scrollState.maxY),
      targetSpeed: scrollState.targetSpeed,
      currentSpeed: Math.round(scrollState.currentSpeed),
      playState: scrollState.playState,
      controlSource: scrollState.controlSource,
      clampedFrames: scrollState.clampedFrameCount,
    },
    voice: voiceState
      ? {
          isMicActive: voiceState.isMicActive,
          isHold: voiceState.isHold,
          vad: {
            isSpeechActive: voiceState.vadState.isSpeechActive,
            rmsLevel: voiceState.vadState.rmsLevel.toFixed(4),
            noiseFloor: voiceState.vadState.noiseFloor.toFixed(4),
            speechMs: voiceState.vadState.speechDurationMs,
            silenceMs: voiceState.vadState.silenceDurationMs,
          },
          scheduler: {
            inFlight: voiceState.schedulerStats.inFlight,
            measuredRtf: voiceState.schedulerStats.measuredRtf.toFixed(3),
            lastLatencyMs: Math.round(voiceState.schedulerStats.lastLatencyMs),
            droppedStale: voiceState.schedulerStats.droppedStaleCount,
            totalDecodes: voiceState.schedulerStats.totalDecodes,
            provisionalDisabled: voiceState.schedulerStats.provisionalDisabled,
          },
          aligner: voiceState.alignerState
            ? {
                pos: voiceState.alignerState.pos.toFixed(1),
                anchorToken: voiceState.alignerState.anchorTokenIndex,
                confidence: voiceState.alignerState.confidence.toFixed(2),
                mode: voiceState.alignerState.mode,
                movement: voiceState.alignerState.movementKind,
                topScore: voiceState.alignerState.debug.topScore.toFixed(2),
                candidatesCount: voiceState.alignerState.debug.candidates.length,
              }
            : null,
          lastTranscript: voiceState.lastTranscript,
        }
      : 'Inactive',
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(diagnosticsData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
      <div className="bg-neutral-900 border border-neutral-700 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl text-neutral-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold">Engine Diagnostics</h2>
            <span className="text-xs bg-blue-900/60 text-blue-300 px-2 py-0.5 rounded-full">
              Live Inspector
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-neutral-800 rounded-lg text-neutral-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6 font-mono text-xs">
          {/* Scroll Engine Block */}
          <div className="bg-neutral-950 p-4 rounded-lg border border-neutral-800">
            <div className="text-neutral-400 font-semibold mb-2">SCROLL ENGINE</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-neutral-300">
              <div>Position Y: <span className="text-white font-bold">{Math.round(scrollState.y)} px</span></div>
              <div>Max Y: <span className="text-white">{Math.round(scrollState.maxY)} px</span></div>
              <div>Current Speed: <span className="text-white">{Math.round(scrollState.currentSpeed)} px/s</span></div>
              <div>Play State: <span className="text-white uppercase">{scrollState.playState}</span></div>
              <div>Source: <span className="text-white uppercase">{scrollState.controlSource}</span></div>
              <div>Clamped Frames: <span className={scrollState.clampedFrameCount > 0 ? "text-amber-400" : "text-white"}>{scrollState.clampedFrameCount}</span></div>
            </div>
          </div>

          {/* Voice Follow & VAD Block */}
          {voiceState && (
            <>
              <div className="bg-neutral-950 p-4 rounded-lg border border-neutral-800">
                <div className="text-neutral-400 font-semibold mb-2">AUDIO & VAD</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-neutral-300">
                  <div>Mic Active: <span className={voiceState.isMicActive ? "text-emerald-400" : "text-red-400"}>{voiceState.isMicActive ? "YES" : "NO"}</span></div>
                  <div>Speech Active: <span className={voiceState.vadState.isSpeechActive ? "text-emerald-400" : "text-neutral-400"}>{voiceState.vadState.isSpeechActive ? "SPEECH" : "SILENCE"}</span></div>
                  <div>RMS Level: <span className="text-white">{voiceState.vadState.rmsLevel.toFixed(4)}</span></div>
                  <div>Noise Floor: <span className="text-white">{voiceState.vadState.noiseFloor.toFixed(4)}</span></div>
                  <div>Speech Duration: <span className="text-white">{voiceState.vadState.speechDurationMs} ms</span></div>
                  <div>Silence Duration: <span className="text-white">{voiceState.vadState.silenceDurationMs} ms</span></div>
                </div>
              </div>

              <div className="bg-neutral-950 p-4 rounded-lg border border-neutral-800">
                <div className="text-neutral-400 font-semibold mb-2">DECODE SCHEDULER & STT</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-neutral-300">
                  <div>In Flight: <span className={voiceState.schedulerStats.inFlight ? "text-amber-400" : "text-neutral-400"}>{voiceState.schedulerStats.inFlight ? "IN_PROGRESS" : "IDLE"}</span></div>
                  <div>Measured RTF: <span className={voiceState.schedulerStats.measuredRtf > 0.8 ? "text-red-400" : "text-emerald-400"}>{voiceState.schedulerStats.measuredRtf.toFixed(2)}</span></div>
                  <div>Last Latency: <span className="text-white">{Math.round(voiceState.schedulerStats.lastLatencyMs)} ms</span></div>
                  <div>Total Decodes: <span className="text-white">{voiceState.schedulerStats.totalDecodes}</span></div>
                  <div>Dropped Stale: <span className="text-white">{voiceState.schedulerStats.droppedStaleCount}</span></div>
                  <div>Provisional: <span className={voiceState.schedulerStats.provisionalDisabled ? "text-amber-400" : "text-emerald-400"}>{voiceState.schedulerStats.provisionalDisabled ? "DISABLED" : "ENABLED"}</span></div>
                </div>
                {voiceState.lastTranscript && (
                  <div className="mt-3 pt-3 border-t border-neutral-800 text-neutral-300">
                    <span className="text-neutral-500">Transcript:</span> <span className="text-white dir-auto font-sans text-sm">{voiceState.lastTranscript}</span>
                  </div>
                )}
              </div>

              {voiceState.alignerState && (
                <div className="bg-neutral-950 p-4 rounded-lg border border-neutral-800">
                  <div className="text-neutral-400 font-semibold mb-2">ALIGNMENT ENGINE</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-neutral-300">
                    <div>Token Pos: <span className="text-white font-bold">{voiceState.alignerState.pos.toFixed(1)}</span></div>
                    <div>Anchor Token: <span className="text-white">{voiceState.alignerState.anchorTokenIndex}</span></div>
                    <div>Confidence: <span className="text-white">{(voiceState.alignerState.confidence * 100).toFixed(0)}%</span></div>
                    <div>Mode: <span className="text-emerald-400 font-bold">{voiceState.alignerState.mode}</span></div>
                    <div>Movement: <span className="text-white">{voiceState.alignerState.movementKind}</span></div>
                    <div>Top Candidate Score: <span className="text-white">{voiceState.alignerState.debug.topScore.toFixed(2)}</span></div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-800 bg-neutral-900/50">
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 rounded text-xs transition"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            <span>{copied ? 'Copied to Clipboard' : 'Copy Diagnostics JSON'}</span>
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-white rounded text-xs font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
